import {FC, MouseEvent} from 'react'
import {TreatmentRequestTherapistDoctor} from '@/features/treatment-requests/types.ts'
import {CheckIcon, Mail01Icon, MarkerPin01Icon, PhoneIcon} from '@components/ui/icon/Icon.tsx'
import {StyledTherapistCard} from '@/features/treatment-requests/components/therapist-doctor-card/style'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {Menu, MenuGroup, MenuItem} from '@components/ui/menu-atoms/MenuAtoms.tsx'
import {useTheme} from 'styled-components'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import dayjs from 'dayjs'

interface TherapistDoctorCardProps {
    id: string
    checked: boolean
    contacts: TreatmentRequestTherapistDoctor['contacts']
    disabled: boolean
    distance?: number
    priCount: number
    availableSlots: number
    specializationName: string
    fullName: string
    onCheck: (isChecked: boolean) => void
}

export const TherapistDoctorCard: FC<TherapistDoctorCardProps> = ({
    id,
    fullName,
    contacts,
    distance,
    priCount,
    availableSlots,
    specializationName,
    checked,
    disabled,
    onCheck
}) => {
    const {t} = useTranslation()
    const theme = useTheme()

    return (
        <StyledTherapistCard
            onClick={disabled ? undefined : () => onCheck(!checked)}
            $checked={checked}
            $disabled={disabled}
        >
            <CheckIcon size={20} />
            <Flexbox direction="column" gap={distance != null ? 0 : 2}>
                <h4>
                    <ButtonLink
                        shape="text"
                        variant="tertiary"
                        onClick={(event: MouseEvent) => event.stopPropagation()}
                        to={`/agenda/users/week/${dayjs().format('DD-MM-YYYY')}?category=custom&subjectIds=${id}`}
                    >
                        {fullName}
                    </ButtonLink>
                </h4>
                {distance != null && (
                    <Flexbox align="center" gap={1}>
                        <MarkerPin01Icon size={12} fill={theme.palette.neutral['950']} />
                        <p>
                            <Trans
                                i18nKey="treatment_requests:distance_label"
                                values={{count: Math.round(distance) || 1}}
                                components={{b: <b />}}
                            />
                        </p>
                    </Flexbox>
                )}
                {specializationName != null && <p>{specializationName}</p>}
            </Flexbox>
            <Flexbox direction="column" justify="space-between" align="flex-end">
                {contacts.length >= 1 && (
                    <MenuProvider>
                        <MenuButton
                            onClick={(event: MouseEvent) => event.stopPropagation()}
                            style={{textDecoration: 'underline', height: 'max-content'}}
                        >
                            {t('commons:contacts')}
                        </MenuButton>
                        <Menu style={{zIndex: theme.zIndex.modal + 1}}>
                            <MenuGroup>
                                {contacts.map((contact, index) => (
                                    <MenuItem
                                        render={
                                            <a
                                                href={
                                                    contact.type == 'email'
                                                        ? `mailto:${contact.contact}`
                                                        : `tel:${contact.contact}`
                                                }
                                            />
                                        }
                                        key={index}
                                    >
                                        {contact.type == 'email' ? <Mail01Icon /> : <PhoneIcon />}
                                        {contact.contact}
                                    </MenuItem>
                                ))}
                            </MenuGroup>
                        </Menu>
                    </MenuProvider>
                )}
                {priCount ? (
                    <p>
                        <Trans
                            i18nKey="treatment_requests:pri_count_label"
                            count={priCount}
                            components={{
                                b: <b />
                            }}
                        />
                    </p>
                ) : (
                    <p>
                        <Trans
                            i18nKey="treatment_requests:available_slots_label"
                            count={availableSlots}
                            components={{
                                b: <b />
                            }}
                        />
                    </p>
                )}
            </Flexbox>
        </StyledTherapistCard>
    )
}
